import { memo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { Chip } from '@mui/material';

import CurrencyFormat from 'components/ui/text-formats/CurrencyFormat';
import ContractInfoTables from './ContractInfoTables';
import CLIENT_TYPE from 'shared/clientTypeList';
import REPAIR_TYPE from 'shared/repairTypeList';
import getLabelByTypeList from 'utils/getLabelByTypeList';
import ImagesSwiper from 'components/ImagesSwiper';
import { NoImageIcon } from 'customIcons';

const StatCard = ({ label, value, icon, accent }) => (
  <div className='flex flex-col gap-1 bg-white rounded-2xl px-4 py-4 border-l-4 shadow-sm min-w-0' style={{ borderLeftColor: accent }}>
    <div className='flex items-center gap-1.5 text-gray-400 text-xs font-medium'>
      <i className={`${icon} text-sm shrink-0`} style={{ color: accent }} />
      <span className='truncate leading-tight'>{label}</span>
    </div>
    <div className='text-gray-800 font-semibold text-sm leading-snug'>{value}</div>
  </div>
);

const InfoRow = ({ label, value, icon }) => (
  <div className='flex items-start gap-3 py-2 border-b border-gray-50 last:border-0'>
    <div className='w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0 mt-0.5'>
      <i className={`${icon} text-indigo-400 text-xs`} />
    </div>
    <div className='flex flex-col min-w-0'>
      <span className='text-xs text-gray-400'>{label}</span>
      <span className='text-sm font-medium text-gray-700 break-words'>{value || '—'}</span>
    </div>
  </div>
);

function ContractViewModern({ contractQuery, paymentsListQuery, id, totalLeftByMonth, refetch, setRefetch }) {
  const { t } = useTranslation();
  const data = contractQuery?.data;
  if (!data) return null;

  const isValute = data?.isvalute;
  const suffix = isValute == '1' ? ' $' : ' UZS';
  const isPhysical = data?.custom?.client_type == CLIENT_TYPE.PHYSICAL?.code;
  const isLegal = data?.custom?.client_type == CLIENT_TYPE.LEGAL?.code;

  const clientName = isPhysical
    ? `${data?.custom?.surname || ''} ${data?.custom?.name || ''} ${data?.custom?.middlename || ''}`.trim()
    : data?.custom?.name;

  const statusColor = data?.status == 3 ? 'error' : data?.status == 2 ? 'success' : 'primary';
  const statusLabel =
    data?.status == 3
      ? t('common.typeList.contractStatus.cancel')
      : data?.status == 2
      ? t('contract.view.completed')
      : t('common.typeList.contractStatus.active');

  const homes = data?.homelist?.length
    ? data.homelist
    : data?.homes
    ? [{ home: data.homes }]
    : [];

  return (
    <div className='grid grid-cols-12 gap-4 px-4 pb-6' style={{ height: 'calc(100vh - 140px)' }}>
      {/* LEFT column: stats + client */}
      <div className='col-span-12 lg:col-span-5 h-full flex flex-col gap-4 overflow-hidden'>

      {/* Stats — 2 kolonna, 3 qator */}
      <div className='grid grid-cols-2 gap-3'>
        {/* 1 — Jami Summa */}
        <StatCard
          label={t('contract.view.header.sum')}
          icon='bi bi-wallet2'
          accent='#6366f1'
          value={
            isValute == '2' ? (
              <span className='flex flex-col gap-0.5'>
                <CurrencyFormat allowNegative value={data?.totalsum} suffix=' UZS' />
                <CurrencyFormat allowNegative value={data?.totalvalute} suffix=' $' />
              </span>
            ) : (
              <CurrencyFormat allowNegative value={data?.sum - data?.discount} suffix={suffix} />
            )
          }
        />
        {/* 2 — Boshlang'ich */}
        <StatCard
          label={t('contract.view.header.startPrice')}
          icon='bi bi-currency-dollar'
          accent='#8b5cf6'
          value={<CurrencyFormat allowNegative value={data?.start_price} suffix={suffix} />}
        />
        {/* 3 — kv/narx */}
        <StatCard
          label='kv/narx'
          icon='bi bi-rulers'
          accent='#0ea5e9'
          value={
            <CurrencyFormat
              allowNegative
              value={data?.square ? ((data?.sum - data?.discount) / data?.square).toFixed(1) : 0}
              suffix={suffix}
            />
          }
        />
        {/* 4 — Oylik Qarzdorlik */}
        <StatCard
          label={t('contract.view.header.monthlyLeft')}
          icon='bi bi-calendar-event-fill'
          accent='#ef4444'
          value={
            totalLeftByMonth ? (
              <CurrencyFormat value={totalLeftByMonth} allowNegative suffix={isValute == '0' ? ' UZS' : ' $'} />
            ) : (
              <span className='text-gray-400 font-normal text-xs'>{t('contract.view.header.noMonthlyLeft')}</span>
            )
          }
        />
        {/* 5 — Jami Tushum (to'langan) */}
        <StatCard
          label={t('payment.add.contract.paidSum')}
          icon='bi bi-check-circle-fill'
          accent='#22c55e'
          value={
            isValute == '2' ? (
              <span className='flex flex-col gap-0.5'>
                <CurrencyFormat allowNegative value={data?.totalsum - data?.leftsum} suffix=' UZS' />
                <CurrencyFormat allowNegative value={data?.totalvalute - data?.leftvalute} suffix=' $' />
              </span>
            ) : (
              <CurrencyFormat allowNegative value={data?.sum - data?.left - data?.discount} suffix={suffix} />
            )
          }
        />
        {/* 6 — Jami Qarzdorlik */}
        <StatCard
          label={t('contract.view.header.leftPrice')}
          icon='bi bi-hourglass-split'
          accent='#f59e0b'
          value={
            isValute == '2' ? (
              <span className='flex flex-col gap-0.5'>
                <CurrencyFormat allowNegative value={data?.leftsum} suffix=' UZS' />
                <CurrencyFormat allowNegative value={data?.leftvalute} suffix=' $' />
              </span>
            ) : (
              <CurrencyFormat allowNegative value={data?.left} suffix={suffix} />
            )
          }
        />
      </div>

      {/* Client card */}
      <div className='bg-white rounded-2xl shadow-sm p-5 flex flex-col gap-3 flex-1 overflow-hidden'>
          {/* Client header */}
          <div className='flex items-center gap-3'>
            <div className='w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-lg shrink-0'>
              {clientName?.[0]?.toUpperCase() || '?'}
            </div>
            <div className='flex flex-col min-w-0'>
              <Link
                to={`/admin/client/view/${data?.custom?.id}`}
                className='no-underline font-semibold text-gray-800 text-base truncate hover:text-indigo-600'
              >
                {clientName}
              </Link>
              <div className='flex items-center gap-2 mt-0.5'>
                <Chip label={statusLabel} color={statusColor} size='small' />
                {data?.type_name?.name && (
                  <Chip label={data.type_name.name} size='small' variant='outlined' color='secondary' />
                )}
              </div>
            </div>
          </div>

          <div className='h-px bg-gray-100' />

          {/* Client info rows */}
          {isPhysical && (
            <>
              <InfoRow label={t('contract.view.clientPhysical.phone')} icon='bi bi-telephone-fill' value={[data?.custom?.phone, data?.custom?.phone2].filter(Boolean).join(', ')} />
              <InfoRow label={t('contract.view.clientPhysical.passportSeries')} icon='bi bi-card-text' value={data?.custom?.detail?.passport_series} />
              <InfoRow label={t('common.fields.pnfl')} icon='bi bi-fingerprint' value={data?.custom?.detail?.pnfl} />
              <InfoRow label={t('contract.view.clientPhysical.birthday')} icon='bi bi-cake2' value={data?.custom?.detail?.birthday ? moment(data.custom.detail.birthday).format('DD.MM.YYYY') : null} />
              <InfoRow label={t('contract.view.clientPhysical.issue')} icon='bi bi-calendar-check' value={data?.custom?.detail?.issue ? moment(data.custom.detail.issue).format('DD.MM.YYYY') : null} />
              <InfoRow label={t('contract.view.clientPhysical.authority')} icon='bi bi-building' value={data?.custom?.detail?.authority} />
              <InfoRow label={t('contract.view.clientPhysical.home')} icon='bi bi-geo-alt-fill' value={data?.custom?.detail?.home} />
            </>
          )}
          {isLegal && (
            <>
              <InfoRow label={t('contract.view.clientLegal.phone')} icon='bi bi-telephone-fill' value={[data?.custom?.phone, data?.custom?.phone2].filter(Boolean).join(', ')} />
              <InfoRow label={t('contract.view.clientLegal.address')} icon='bi bi-geo-alt-fill' value={data?.custom?.detail?.home} />
              <InfoRow label={t('contract.view.clientLegal.accountNumber')} icon='bi bi-bank' value={data?.custom?.detail?.account_number} />
              <InfoRow label={t('contract.view.clientLegal.tin')} icon='bi bi-hash' value={data?.custom?.detail?.inn} />
              <InfoRow label={t('contract.view.clientLegal.mfo')} icon='bi bi-hash' value={data?.custom?.detail?.mfo} />
              <InfoRow label={t('contract.view.clientLegal.oked')} icon='bi bi-hash' value={data?.custom?.detail?.oked} />
            </>
          )}

          <div className='h-px bg-gray-100' />

          {/* Staff / operator */}
          {data?.staff?.name && (
            <div className='flex items-center gap-2 text-sm'>
              <span className='w-6 h-6 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center text-xs'>
                <i className='bi bi-person-badge' />
              </span>
              <span className='text-gray-400 shrink-0'>{t('contract.view.staff')}:</span>
              <span className='font-medium text-gray-700'>{data.staff.name}</span>
            </div>
          )}
          {data?.operator?.name && (
            <div className='flex items-center gap-2 text-sm'>
              <span className='w-6 h-6 rounded-full bg-blue-100 text-blue-500 flex items-center justify-center text-xs'>
                <i className='bi bi-headset' />
              </span>
              <span className='text-gray-400 shrink-0'>{t('contract.view.operator')}:</span>
              <span className='font-medium text-gray-700'>{data.operator.name}</span>
            </div>
          )}
          {data?.comment && (
            <div className='rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-600 border border-gray-100'>
              <span className='font-medium text-indigo-500'>{t('contract.view.header.comment')}: </span>
              {data.comment}
            </div>
          )}
      </div>
      </div>{/* end LEFT column */}

      {/* RIGHT column: homes + tables — scrolls independently */}
      <div className='col-span-12 lg:col-span-7 h-full flex flex-col gap-4 overflow-y-auto'>
          {homes.map(({ home, square }, idx) => {
            square = Number(square);
            const imgs = [home?.plan?.link, home?.plan?.link2d, home?.plan?.location].filter(Boolean);

            return (
              <div key={home?.id || idx} className='bg-white rounded-2xl shadow-sm p-5 flex gap-4'>
                {/* Image */}
                <div className='w-40 h-40 shrink-0 rounded-xl overflow-hidden bg-gray-50 border border-gray-100 flex items-center justify-center'>
                  {imgs.length ? (
                    <ImagesSwiper removeNavigation imageURLs={imgs} className='!h-40 w-40 object-contain' alt='' />
                  ) : (
                    <NoImageIcon />
                  )}
                </div>

                {/* Details */}
                <div className='flex flex-col gap-1.5 flex-1 min-w-0'>
                  <div className='font-semibold text-gray-700 text-sm mb-1'>
                    {home?.blocks?.objects?.name} — {home?.blocks?.name}
                  </div>
                  {[
                    { label: t('contract.view.homeDetail.homeNumber'), value: home?.number, icon: 'bi bi-door-open' },
                    { label: t('contract.view.homeDetail.stage'), value: home?.stage, icon: 'bi bi-layers' },
                    { label: t('contract.view.homeDetail.rooms'), value: home?.rooms, icon: 'bi bi-grid-1x2' },
                    {
                      label: t('contract.view.homeDetail.area'),
                      icon: 'bi bi-aspect-ratio',
                      value: home?.square != square ? (
                        <>
                          <span className='line-through text-gray-400 mr-1'>{home?.square} <Trans i18nKey='common.global.meter'>m<sup>2</sup></Trans></span>
                          {square} <Trans i18nKey='common.global.meter'>m<sup>2</sup></Trans>
                        </>
                      ) : (
                        <>{home?.square} <Trans i18nKey='common.global.meter'>m<sup>2</sup></Trans></>
                      ),
                    },
                    { label: t('contract.view.homeDetail.isrepaired'), value: getLabelByTypeList(REPAIR_TYPE, home?.isrepaired) || '—', icon: 'bi bi-tools' },
                  ].map((row, i) => (
                    <div key={i} className='flex items-center gap-2 text-xs'>
                      <i className={`${row.icon} text-gray-300 w-4`} />
                      <span className='text-gray-400 shrink-0'>{row.label}:</span>
                      <span className='font-medium text-gray-700'>{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Tables */}
          {id && (
            <ContractInfoTables
              id={id}
              contractQuery={contractQuery}
              paymentsListQuery={paymentsListQuery}
              refetch={refetch}
              setRefetch={setRefetch}
            />
          )}
      </div>
    </div>
  );
}

export default memo(ContractViewModern);
